import Layer from "@/classes/Layer.js";
import { createButton } from "@/utils.js";

const SUPPORTS_FULLSCREEN_CLASS = "supports-fullscreen";

export default class ControlsLayer extends Layer {
  constructor(spinner, node, subjectNode) {
    super(spinner, node, subjectNode);

    /**
     * @type {Record<string, HTMLDivElement>}
     */
    this.groups = {
      actions: null,
      layers: null,
      popups: null,
    };
    this.layerButtons = {};
    this._activeLayerButton = null;
    this.menuButton = null;
    this.zoomInButton = null;
    this.zoomOutButton = null;
    this._zoomControlsLayerChangeHandler = null;

    this.reset();
  }

  buildGroups() {
    for (const g in this.groups) {
      const div = document.createElement("div");
      div.className = g;
      this.appendTo(div, "buttons");
      this.groups[g] = div;
    }
  }

  updateFullscreenSupport() {
    if (this.spinner.supportsFullscreen && document.fullscreenEnabled) {
      this.spinner.spinnerNode.classList.add(SUPPORTS_FULLSCREEN_CLASS);
    }
  }

  reset() {
    this.subjectChildren.buttons.innerHTML = "";
    this.spinner.spinnerNode.classList.remove(SUPPORTS_FULLSCREEN_CLASS);

    this.spinner.off("layerchange", this._zoomControlsLayerChangeHandler);

    this.updateFullscreenSupport();
    this.buildGroups();
  }

  /**
   *
   * @param {string} id
   * @param {"actions"|"layers"|"popups"} group
   * @param {Function} action
   * @returns {HTMLButtonElement}
   */
  addButton(id, group, action) {
    const btn = createButton(id);

    btn.onpointerup = (event) => {
      if (event.button === 2) return;

      action.call(this, event);
    };

    this.groups[group].appendChild(btn);

    if (this.groups.layers.children.length > 2) {
      this.subject.classList.remove("always-expand");
    } else {
      this.subject.classList.add("always-expand");
    }

    return btn;
  }

  addLayerButton(layer) {
    const btn = this.addButton(layer, "layers", () => {
      this.spinner
        .setActiveLayer(layer)
        .then(() => {
          this.spinner.renderScene();
        })
        .catch(console.error);

      this.setActiveLayerButton(layer);
    });

    this.layerButtons[layer] = btn;

    return btn;
  }

  setActiveLayerButton(layer) {
    if (this._activeLayerButton) {
      this._activeLayerButton.classList.remove("active");
    }

    this._activeLayerButton = this.layerButtons[layer];
    if (this._activeLayerButton) {
      this._activeLayerButton.classList.add("active");
    }
  }

  buildMenuControl() {
    this.menuButton = this.addButton("menu", "actions", () => {
      this.subject.classList.add("inert");
      requestAnimationFrame(() => {
        this.subject.classList.add("expand");
        setTimeout(() => {
          this.subject.classList.remove("inert");
        }, 500);
      });
    });
    this.menuButton.tabIndex = -1;
  }

  buildFullscreenControl() {
    if (!document.fullscreenEnabled) return;

    this.fullscreenButton = this.addButton("fullscreen", "actions", () => {
      this.spinner.uiFullscreenEnabled = true;
    });
    this.fullscreenButton.classList.add("fullscreen-supported", "window-only");

    this.closeFullscreenButton = this.addButton(
      "close-fullscreen",
      "actions",
      () => {
        this.spinner.uiFullscreenEnabled = false;
      },
    );
    this.closeFullscreenButton.classList.add(
      "fullscreen-supported",
      "fullscreen-only",
    );
  }

  buildZoomControl() {
    const spinner = this.spinner;

    this.zoomOutButton = this.addButton("zoom-out", "actions", () => {
      spinner.setZoomScale(
        spinner.sceneWidth / 2,
        spinner.sceneHeight / 2,
        spinner.zoomScale - 1,
      );
    });
    this.zoomOutButton.classList.add("desktop-only");

    this.zoomInButton = this.addButton("zoom-in", "actions", () => {
      spinner.setZoomScale(
        spinner.sceneWidth / 2,
        spinner.sceneHeight / 2,
        spinner.zoomScale + 1,
      );
    });
    this.zoomInButton.classList.add("desktop-only");

    this._zoomControlsLayerChangeHandler = (event) => {
      if (event.detail.layer.type === "interior") {
        this.zoomInButton.disabled = true;
        this.zoomOutButton.disabled = true;
      } else {
        this.zoomInButton.disabled = false;
        this.zoomOutButton.disabled = false;
      }
    };

    this.spinner.on("layerchange", this._zoomControlsLayerChangeHandler);
  }
}
